import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Fab,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Person,
  Bed,
  LocalHospital,
  Description,
  NavigateNext,
  NavigateBefore,
  CheckCircle,
  PersonAdd,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createICUAdmission,
  getAvailableICUBeds,
} from "../../../../api/icu";
import { getEmergencyVisit } from "../../../../api/emergency";
import useFetchObjects from "../../../../api/useFetchObjects";
import TicketSearch from "../../../../Components/TicketSearch";
import TicketSelectionView from "../../../../Components/TicketSelectionView";
import axios from "axios";

const NewAdmission = () => {
  const { t } = useTranslation();
  
  const steps = [
    t("icu.admissions.steps.patient_selection", "Patient Selection"),
    t("icu.admissions.steps.icu_bed_assignment", "ICU Bed Assignment"),
    t("icu.admissions.steps.nurse_assignment", "Nurse Assignment"),
    t("icu.admissions.steps.admission_details", "Admission Details"),
    t("icu.admissions.steps.review", "Review"),
  ];
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const emergencyVisitId = searchParams.get("emergency");

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    patient: null,
    bed: null,
    admission_type: emergencyVisitId ? 1 : 2, // Emergency if from emergency, otherwise Elective
    admission_diagnosis: "",
    admitting_doctor: auth?.user?.user?.id || "",
    department: "",
    expected_discharge_date: "",
    admission_notes: "",
    emergency_visit: null,
    ticket: null,
    icu_indication: "",
    severity_score: "",
    nurse_assignments: [], // Array of {nurse, shift, role}
  });
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [errors, setErrors] = useState({});
  const [emergencyVisit, setEmergencyVisit] = useState(null);
  const [loadingEmergency, setLoadingEmergency] = useState(false);

  // Current nurse assignment form
  const [currentNurseAssignment, setCurrentNurseAssignment] = useState({
    nurse: null,
    shift: 1, // Morning
    role: 1, // Primary
    notes: "",
  });

  // Fetch data
  const {
    data: patients,
    isLoading: patientsLoading,
    error: patientsError,
  } = useFetchObjects(["patients"], "patients/?page_size=1000", token);

  const {
    data: doctors,
    isLoading: doctorsLoading,
    error: doctorsError,
  } = useFetchObjects(
    ["users"],
    "users/?page_size=1000",
    token
  );

  const {
    data: departments,
    isLoading: departmentsLoading,
    error: departmentsError,
  } = useFetchObjects(["departments"], "departments/?page_size=1000", token);

  const {
    data: nurses,
    isLoading: nursesLoading,
    error: nursesError,
  } = useFetchObjects(
    ["nurses"],
    "users/?page_size=1000", // We'll filter nurses on frontend or backend
    token
  );

  const {
    data: availableBeds,
    isLoading: bedsLoading,
    error: bedsError,
  } = useQuery({
    queryKey: ["available-icu-beds"],
    queryFn: () => getAvailableICUBeds({}, token),
  });

  // Filter doctors and nurses (include admins for testing)
  const filteredDoctors = Array.isArray(doctors) ? doctors : doctors?.results || [];
  const filteredNurses = (Array.isArray(nurses) ? nurses : nurses?.results || []).filter(
    (user) => user.nurse === true || user.is_admin === true
  );

  // Fetch emergency visit data if coming from Emergency
  useEffect(() => {
    const fetchEmergencyVisit = async () => {
      if (!emergencyVisitId || !token) return;

      try {
        setLoadingEmergency(true);
        const emergencyData = await getEmergencyVisit(emergencyVisitId, token);
        setEmergencyVisit(emergencyData);

        // Pre-fill form data from emergency visit
        if (emergencyData) {
          // Find patient in patients list
          const patientList = Array.isArray(patients)
            ? patients
            : patients?.results || [];
          const matchedPatient = emergencyData.patient
            ? patientList.find((p) => p.id === emergencyData.patient)
            : null;

          // Find attending doctor in doctors list
          const doctorsList = Array.isArray(doctors)
            ? doctors
            : doctors?.results || [];
          const matchedDoctor = emergencyData.attending_doctor
            ? doctorsList.find((d) => d.id === emergencyData.attending_doctor)
            : null;

          // Pre-fill form data
          setFormData((prev) => ({
            ...prev,
            patient: matchedPatient || null,
            admission_diagnosis: emergencyData.diagnosis || "",
            admitting_doctor: emergencyData.attending_doctor || prev.admitting_doctor,
            emergency_visit: emergencyData,
            ticket: emergencyData.ticket || null, // Pass ticket from emergency visit
            admission_type: 1, // Emergency admission type
            icu_indication: emergencyData.chief_complaint || "",
            admission_notes: emergencyData.chief_complaint
              ? `Chief Complaint: ${emergencyData.chief_complaint}`
              : prev.admission_notes,
          }));
          
          // If emergency visit has a ticket, set it in selectedTicket for display
          if (emergencyData.ticket) {
            // Fetch ticket details to show in TicketSelectionView
            try {
              const ticketResponse = await axios.get(
                `${process.env.REACT_APP_API_URL || ""}/tickets/${emergencyData.ticket}/`,
                {
                  headers: { Authorization: `Token ${token}` },
                }
              );
              setSelectedTicket(ticketResponse.data);
            } catch (err) {
              console.error("Error fetching ticket:", err);
            }
          }

          // Skip to Step 1 (Bed Selection) if patient is found
          if (matchedPatient) {
            setActiveStep(1);
          }
        }
      } catch (error) {
        console.error("Error fetching emergency visit:", error);
      } finally {
        setLoadingEmergency(false);
      }
    };

    // Wait for patients and doctors to load before fetching emergency
    if (
      emergencyVisitId &&
      token &&
      !patientsLoading &&
      !doctorsLoading &&
      patients &&
      doctors
    ) {
      fetchEmergencyVisit();
    }
  }, [
    emergencyVisitId,
    token,
    patients,
    doctors,
    patientsLoading,
    doctorsLoading,
  ]);

  // Create admission mutation
  const createMutation = useMutation({
    mutationFn: (data) => createICUAdmission(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["icu-admissions"] });
      queryClient.invalidateQueries({ queryKey: ["icu-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["available-icu-beds"] });
      navigate("/icu/admissions");
    },
  });

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Patient Selection
        if (!formData.patient) {
          newErrors.patient = t("icu.admissions.errors.patient_required");
        }
        break;
      case 1: // Bed Assignment
        if (!formData.bed) {
          newErrors.bed = t("icu.admissions.errors.bed_required");
        }
        break;
      case 2: // Nurse Assignment
        if (formData.nurse_assignments.length === 0) {
          newErrors.nurse_assignments = t("icu.admissions.errors.nurse_required");
        }
        break;
      case 3: // Admission Details
        if (!formData.admission_diagnosis) {
          newErrors.admission_diagnosis = t("icu.admissions.errors.diagnosis_required");
        }
        if (!formData.admitting_doctor) {
          newErrors.admitting_doctor = t("icu.admissions.errors.doctor_required");
        }
        if (!formData.department) {
          newErrors.department = t("icu.admissions.errors.department_required");
        }
        if (!formData.icu_indication) {
          newErrors.icu_indication = t("icu.admissions.errors.icu_indication_required");
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleAddNurse = () => {
    if (!currentNurseAssignment.nurse) {
      setErrors({ ...errors, nurse_assignments: "Please select a nurse" });
      return;
    }

    setFormData({
      ...formData,
      nurse_assignments: [
        ...formData.nurse_assignments,
        {
          nurse: currentNurseAssignment.nurse.id,
          shift: currentNurseAssignment.shift,
          role: currentNurseAssignment.role,
          notes: currentNurseAssignment.notes,
        },
      ],
    });

    setCurrentNurseAssignment({
      nurse: null,
      shift: 1,
      role: formData.nurse_assignments.length === 0 ? 1 : 2, // First is primary, rest are secondary
      notes: "",
    });
    setErrors({ ...errors, nurse_assignments: "" });
  };

  const handleRemoveNurse = (index) => {
    const newAssignments = [...formData.nurse_assignments];
    newAssignments.splice(index, 1);
    setFormData({ ...formData, nurse_assignments: newAssignments });
  };

  const handleSubmit = () => {
    if (validateStep(activeStep)) {
      const submitData = {
        ...formData,
        patient: formData.patient?.id,
        bed: formData.bed?.id,
        admitting_doctor: formData.admitting_doctor,
        department: formData.department,
        emergency_visit: formData.emergency_visit?.id || null,
        ticket: formData.ticket || null, // Send ticket ID if exists
        nurse_assignments: formData.nurse_assignments,
      };

      createMutation.mutate(submitData);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.select_patient", "Select Patient")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("icu.admissions.ticket_search_hint", "Search by ticket number (optional) or select patient directly")}
              </Typography>
              
              {/* Ticket Search */}
              <Box sx={{ mb: 3 }}>
                <TicketSearch
                  value={selectedTicket}
                  onChange={(ticket) => {
                    setSelectedTicket(ticket);
                    if (ticket && ticket.patient) {
                      // Find patient from ticket
                      const patient = patients?.results?.find(p => p.id === ticket.patient);
                      if (patient) {
                        setFormData({ 
                          ...formData, 
                          patient: patient,
                          ticket: ticket.id,
                          department: ticket.department || formData.department
                        });
                        setErrors({ ...errors, patient: "" });
                      }
                    } else {
                      setFormData({ ...formData, ticket: null });
                    }
                  }}
                  label={t("icu.admissions.ticket_number", "Ticket Number (Optional)")}
                  placeholder={t("icu.admissions.search_ticket", "Search by ticket number...")}
                />
              </Box>

              {selectedTicket && (
                <Box sx={{ mb: 3 }}>
                  <TicketSelectionView ticket={selectedTicket} />
                </Box>
              )}

              {/* Patient Search (fallback if no ticket) */}
              {patientsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : patientsError ? (
                <Alert severity="error">
                  {t("icu.admissions.error_loading")} - {patientsError.message}
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                    {t("icu.admissions.or_select_patient", "Or select patient directly:")}
                  </Typography>
                  <Autocomplete
                    options={patients?.results || []}
                    getOptionLabel={(option) =>
                      `${option.firstname} ${option.lastname || ""}`
                    }
                    value={formData.patient}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, patient: newValue, ticket: null });
                      setSelectedTicket(null);
                      setErrors({ ...errors, patient: "" });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("icu.admissions.patient", "Patient")}
                        error={!!errors.patient}
                        helperText={errors.patient}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <Person />,
                        }}
                      />
                    )}
                  />
                </>
              )}
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.select_icu_bed", "Select ICU Bed")}
              </Typography>
              {bedsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : bedsError ? (
                <Alert severity="error">
                  {t("icu.admissions.error_loading")} - {bedsError.message}
                </Alert>
              ) : !availableBeds || availableBeds.length === 0 ? (
                <Alert severity="warning">
                  {t("icu.admissions.no_beds_available", "No ICU beds available. Please contact the administrator.")}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {availableBeds?.map((bed) => (
                    <Grid item xs={12} sm={6} md={4} key={bed.id}>
                      <Card
                        sx={{
                          cursor: "pointer",
                          border: formData.bed?.id === bed.id ? 2 : 1,
                          borderColor:
                            formData.bed?.id === bed.id
                              ? "primary.main"
                              : "divider",
                          "&:hover": {
                            borderColor: "primary.main",
                            transform: "translateY(-2px)",
                            boxShadow: 3,
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                        onClick={() => {
                          setFormData({ ...formData, bed: bed });
                          setErrors({ ...errors, bed: "" });
                        }}
                      >
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                          >
                            <Bed color="primary" />
                            <Typography variant="h6">
                              {bed.bed_number}
                            </Typography>
                            {formData.bed?.id === bed.id && (
                              <CheckCircle color="primary" fontSize="small" />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Room: {bed.room_number} | Ward: {bed.ward_name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {errors.bed && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.bed}
                </Alert>
              )}
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.assign_nurses", "Assign Nurses")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("icu.admissions.nurse_assignment_description", "Assign at least one primary nurse. You can assign multiple nurses for different shifts.")}
              </Typography>

              {/* Add Nurse Form */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={filteredNurses}
                      getOptionLabel={(option) =>
                        `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
                        option.username ||
                        ""
                      }
                      value={currentNurseAssignment.nurse}
                      onChange={(event, newValue) => {
                        setCurrentNurseAssignment({
                          ...currentNurseAssignment,
                          nurse: newValue,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("icu.admissions.nurse", "Nurse")}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>{t("icu.shifts.label", "Shift")}</InputLabel>
                      <Select
                        value={currentNurseAssignment.shift}
                        onChange={(e) =>
                          setCurrentNurseAssignment({
                            ...currentNurseAssignment,
                            shift: e.target.value,
                          })
                        }
                      >
                        <MenuItem value={1}>
                          {t("icu.shifts.morning", "Morning")}
                        </MenuItem>
                        <MenuItem value={2}>
                          {t("icu.shifts.evening", "Evening")}
                        </MenuItem>
                        <MenuItem value={3}>
                          {t("icu.shifts.night", "Night")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>{t("icu.nurses.role", "Role")}</InputLabel>
                      <Select
                        value={currentNurseAssignment.role}
                        onChange={(e) =>
                          setCurrentNurseAssignment({
                            ...currentNurseAssignment,
                            role: e.target.value,
                          })
                        }
                      >
                        <MenuItem value={1}>
                          {t("icu.nurses.primary", "Primary")}
                        </MenuItem>
                        <MenuItem value={2}>
                          {t("icu.nurses.secondary", "Secondary")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PersonAdd />}
                      onClick={handleAddNurse}
                      sx={{ height: "56px" }}
                    >
                      {t("icu.admissions.add_nurse", "Add Nurse")}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Assigned Nurses List */}
              {formData.nurse_assignments.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("icu.admissions.assigned_nurses", "Assigned Nurses")}
                  </Typography>
                  {formData.nurse_assignments.map((assignment, index) => {
                    const nurse = filteredNurses.find((n) => n.id === assignment.nurse);
                    return (
                      <Chip
                        key={index}
                        label={`${nurse?.first_name || ""} ${nurse?.last_name || ""} - ${t(`icu.shifts.${["morning", "evening", "night"][assignment.shift - 1]}`, ["Morning", "Evening", "Night"][assignment.shift - 1])} (${t(`icu.nurses.${["primary", "secondary"][assignment.role - 1]}`, ["Primary", "Secondary"][assignment.role - 1])})`}
                        onDelete={() => handleRemoveNurse(index)}
                        color={assignment.role === 1 ? "primary" : "default"}
                        sx={{ m: 0.5 }}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info">
                  {t("icu.admissions.no_nurses_assigned", "No nurses assigned")}
                </Alert>
              )}
              {errors.nurse_assignments && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.nurse_assignments}
                </Alert>
              )}
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.admission_type}>
                <InputLabel>{t("icu.admissions.type_label", "Admission Type")}</InputLabel>
                <Select
                  value={formData.admission_type}
                  onChange={(e) =>
                    setFormData({ ...formData, admission_type: e.target.value })
                  }
                >
                  <MenuItem value={1}>
                    {t("icu.admissions.type.emergency", "Emergency")}
                  </MenuItem>
                  <MenuItem value={2}>
                    {t("icu.admissions.type.post_operative", "Post-Operative")}
                  </MenuItem>
                  <MenuItem value={3}>
                    {t("icu.admissions.type.medical", "Medical")}
                  </MenuItem>
                  <MenuItem value={4}>
                    {t("icu.admissions.type.transfer_from_ipd", "Transfer from IPD")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.admitting_doctor}>
                <InputLabel>{t("icu.admissions.admitting_doctor", "Admitting Doctor")}</InputLabel>
                <Select
                  value={formData.admitting_doctor}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      admitting_doctor: e.target.value,
                    })
                  }
                  disabled={doctorsLoading}
                >
                  {filteredDoctors.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                      {user.username && ` (${user.username})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.department}>
                <InputLabel>{t("icu.admissions.department", "Department")}</InputLabel>
                <Select
                  value={formData.department}
                  onChange={(e) =>
                    setFormData({ ...formData, department: e.target.value })
                  }
                  disabled={departmentsLoading}
                >
                  {(Array.isArray(departments)
                    ? departments
                    : departments?.results || []
                  )?.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("icu.admissions.expected_discharge_date", "Expected Discharge Date")}
                type="date"
                value={formData.expected_discharge_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expected_discharge_date: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("icu.admissions.icu_indication", "ICU Indication")}
                multiline
                rows={2}
                value={formData.icu_indication}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    icu_indication: e.target.value,
                  });
                  setErrors({ ...errors, icu_indication: "" });
                }}
                error={!!errors.icu_indication}
                helperText={errors.icu_indication || t("icu.admissions.icu_indication_helper", "Reason for ICU admission")}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("icu.admissions.severity_score_optional", "Severity Score (Optional)")}
                value={formData.severity_score}
                onChange={(e) =>
                  setFormData({ ...formData, severity_score: e.target.value })
                }
                placeholder={t("icu.admissions.severity_score_placeholder", "e.g., APACHE II, SOFA")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("icu.admissions.diagnosis", "Diagnosis")}
                multiline
                rows={3}
                value={formData.admission_diagnosis}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    admission_diagnosis: e.target.value,
                  });
                  setErrors({ ...errors, admission_diagnosis: "" });
                }}
                error={!!errors.admission_diagnosis}
                helperText={errors.admission_diagnosis}
                required
                InputProps={{
                  startAdornment: <Description />,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("icu.admissions.notes", "Admission Notes")}
                multiline
                rows={3}
                value={formData.admission_notes}
                onChange={(e) =>
                  setFormData({ ...formData, admission_notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.review_admission", "Review Admission")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.patient_info", "Patient Information")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Person color="primary" />
                    <Typography variant="body1">
                      {formData.patient?.firstname} {formData.patient?.lastname}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formData.patient?.phone}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.bed_info", "ICU Bed Information")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Bed color="primary" />
                    <Typography variant="body1">
                      {formData.bed?.bed_number}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formData.bed?.room_number} - {formData.bed?.ward_name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.nurse_assignments", "Assigned Nurses")}
                  </Typography>
                  {formData.nurse_assignments.length > 0 ? (
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {formData.nurse_assignments.map((assignment, index) => {
                        const nurse = filteredNurses.find((n) => n.id === assignment.nurse);
                        return (
                          <Chip
                            key={index}
                            label={`${nurse?.first_name || ""} ${nurse?.last_name || ""} - ${t(`icu.shifts.${["morning", "evening", "night"][assignment.shift - 1]}`, ["Morning", "Evening", "Night"][assignment.shift - 1])} (${t(`icu.nurses.${["primary", "secondary"][assignment.role - 1]}`, ["Primary", "Secondary"][assignment.role - 1])})`}
                            color={assignment.role === 1 ? "primary" : "default"}
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("icu.admissions.no_nurses_assigned", "No nurses assigned")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.admission_details", "Admission Details")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("icu.admissions.type_label", "Admission Type")}:
                      </Typography>
                      <Typography variant="body1">
                        {formData.admission_type === 1 &&
                          t("icu.admissions.type.emergency", "Emergency")}
                        {formData.admission_type === 2 &&
                          t("icu.admissions.type.post_operative", "Post-Operative")}
                        {formData.admission_type === 3 &&
                          t("icu.admissions.type.medical", "Medical")}
                        {formData.admission_type === 4 &&
                          t("icu.admissions.type.transfer_from_ipd", "Transfer from IPD")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("icu.admissions.icu_indication", "ICU Indication")}:
                      </Typography>
                      <Typography variant="body1">
                        {formData.icu_indication}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {t("icu.admissions.diagnosis", "Diagnosis")}:
                      </Typography>
                      <Typography variant="body1">
                        {formData.admission_diagnosis}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/icu/admissions")}
        >
          {t("common.back", "Back")}
        </Button>
        <Typography variant="h4" component="h1">
          {t("icu.admissions.new_admission", "New ICU Admission")}
        </Typography>
        {emergencyVisit && (
          <Chip
            label={t("icu.admissions.from_emergency", "From Emergency")}
            color="warning"
            variant="outlined"
            sx={{ ml: "auto" }}
          />
        )}
        {loadingEmergency && (
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: "auto" }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t("icu.admissions.loading_emergency", "Loading emergency visit...")}
            </Typography>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Progress Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">{label}</Typography>
                  {index < activeStep && (
                    <CheckCircle color="success" fontSize="small" />
                  )}
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Navigation Buttons */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBefore />}
            variant="outlined"
          >
            {t("common.previous", "Previous")}
          </Button>

          <Box display="flex" gap={2}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={createMutation.isPending}
                startIcon={
                  createMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Save />
                  )
                }
                size="large"
                color="primary"
              >
                {createMutation.isPending
                  ? t("common.saving", "Saving...")
                  : t("common.save", "Save")}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NavigateNext />}
                size="large"
                color="primary"
              >
                {t("common.next", "Next")}
              </Button>
            )}
          </Box>
        </Box>

        {/* Step Content */}
        {renderStepContent(activeStep)}

        {/* Error Display */}
        {createMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createMutation.error?.message ||
              t("icu.admissions.error_creating", "Error creating admission")}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default NewAdmission;

